/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */

import { fg } from '@atlassian/jira-feature-gating';
import { isStreamingEnabled } from '@jira/ssr__streaming-enabled/src/isStreamingEnabled.tsx';
import type {
	RouterContext,
	ResourceDependencies,
	RouteResource,
	ResourceStoreContext,
} from '../../../../../common/types.tsx';
import type { GetResourceOptions, PrefetchSlice } from '../../types.tsx';
import { DEFAULT_RESOURCE_MAX_AGE } from '../create-resource/constants.tsx';
import { getExpiresAt } from '../expires-at/index.tsx';
import { generateTimeGuard } from '../generate-time-guard/index.tsx';
import { TimeoutError } from '../timeout-error/index.tsx';
import { DEFAULT_PREFETCH_MAX_AGE } from './constants.tsx';

export function createLoadingSlice({
	context,
	dependencies,
	options,
	resource,
	routerStoreContext,
}: {
	context: ResourceStoreContext;
	dependencies: () => ResourceDependencies;
	options: GetResourceOptions;
	resource: RouteResource;
	routerStoreContext: RouterContext;
}): PrefetchSlice {
	const { type, getData } = resource;
	const { prefetch, timeout, abortSignal } = options;

	// hard errors in dependencies or getData are converted into softer async error
	let promiseOrData: unknown | Promise<unknown>;
	try {
		promiseOrData = getData(
			{
				...routerStoreContext,
				isPrefetch: !!prefetch,
				dependencies: dependencies(),
			},
			context,
		);
	} catch (error) {
		promiseOrData = Promise.reject(error);
	}

	// ensure the promise includes any timeout error
	const timeoutGuard = timeout ? generateTimeGuard(timeout) : null;

	// check if getData was sync, by looking for a Promise-like shape
	const data = typeof (promiseOrData as any)?.then === 'function' ? undefined : promiseOrData;
	let promise: Promise<unknown>;
	if (isStreamingEnabled() || fg('jfp-magma-ssr-streaming-cleanup')) {
		// New logic also supports using abortSignal
		if (data !== undefined) {
			// Data is sync, short-circuit resolve
			promise = Promise.resolve(data);
		} else if (timeout === undefined && abortSignal === undefined) {
			promise = promiseOrData as Promise<unknown>;
		} else {
			promise = new Promise((resolve, reject) => {
				const rejectWithTimeout = () => {
					reject(new TimeoutError(type));
					cleanup();
				};
				const cleanup = () => {
					if (timeoutGuard?.timerId) {
						clearTimeout(timeoutGuard.timerId);
					}
					if (abortSignal) {
						abortSignal.removeEventListener('abort', rejectWithTimeout);
					}
				};

				// Wrap with `Promise.resolve` to ensure is a promise
				Promise.resolve(promiseOrData).then(resolve, reject).then(cleanup, cleanup);
				if (timeoutGuard?.promise) {
					timeoutGuard.promise.then(rejectWithTimeout);
				}
				if (abortSignal) {
					// Reuse same `TimeoutError` as timeout option, assuming that's the only use case for aborts
					abortSignal.addEventListener('abort', rejectWithTimeout);
				}
			});
		}
	} else {
		// oxlint-disable-next-line no-nested-ternary
		promise = timeout
			? Promise.race([promiseOrData, timeoutGuard?.promise]).then((maybeData) => {
					if (timeoutGuard && !timeoutGuard.isPending) {
						throw new TimeoutError(type);
					}
					timeoutGuard?.timerId && clearTimeout(timeoutGuard.timerId);

					return maybeData;
				})
			: // if we already have a result, wrap it so consumers can access it via same API
				data !== undefined
				? Promise.resolve(data)
				: (promiseOrData as Promise<unknown>);
	}

	const resourceMaxAge = getExpiresAt(resource.maxAge ?? DEFAULT_RESOURCE_MAX_AGE);

	return {
		promise,
		data,
		expiresAt: prefetch
			? Math.max(resourceMaxAge, getExpiresAt(DEFAULT_PREFETCH_MAX_AGE))
			: resourceMaxAge,
	};
}
